import { useParams } from "react-router-dom";
import { COMMENT_MODE } from "context/CustomFormContext";
import { useStores } from "stores";

import { Comment, CommentForm, PrimaryFormNames, ReleaseStatus, SecondaryFormNames } from "types";

import { ReleaseUrlParams } from "../types";

import { useFetchComments } from "./useFetchComments";
import { ReturnReleaseFormState, usePrimaryFormState } from "./usePrimaryFormState";

interface SecondaryFormStateCbProps {
  secondaryFormName: SecondaryFormNames; // The name of the secondary form
  secondaryFormId: string; // The unique identifier for the secondary form
  filterCommentKey: keyof Comment; // The key in the comment object used to filter comments
}

/**
 * Hook for managing the state and comment logic of secondary forms.
 *
 * This hook extends the logic of the primary form by introducing dynamic
 * handling of comments for secondary forms based on user roles and release status.
 *
 * @returns A function that takes properties for a secondary form and returns its state.
 */
export const useSecondaryFormState = (): ((props: SecondaryFormStateCbProps) => ReturnReleaseFormState) => {
  const {
    userStore: { isAdmin },
    revisedFormStore: {
      release,
      comments: { postSecondaryComment, deleteSecondaryComment, patchSecondaryComment },
    },
  } = useStores();

  const { updateCommentState } = useFetchComments(); // Utility to update the comment state
  const { userId = null, releaseId } = useParams<ReleaseUrlParams>(); // Extract user and release IDs from URL parameters

  const formState = usePrimaryFormState(); // Get the base state from the primary form

  /**
   * Comment callback function for secondary forms.
   *
   * Handles comment actions (add, edit, delete) for secondary forms and updates the comment state.
   *
   * @param {SecondaryFormStateCbProps} props - Configuration for the secondary form.
   * @returns A function to handle comments based on the provided mode.
   */
  const commentCallback =
    ({ secondaryFormName, secondaryFormId, filterCommentKey }: SecondaryFormStateCbProps) =>
    async (comment: CommentForm, mode: COMMENT_MODE) => {
      if (!releaseId) return; // Ensure `releaseId` is available

      try {
        switch (mode) {
          case COMMENT_MODE.ADD:
            // Add a secondary comment
            await postSecondaryComment(userId, PrimaryFormNames.Releases, releaseId, secondaryFormName, secondaryFormId, comment);
            break;
          case COMMENT_MODE.EDIT:
            // Edit a secondary comment
            await patchSecondaryComment(
              userId,
              PrimaryFormNames.Releases,
              releaseId,
              secondaryFormName,
              secondaryFormId,
              comment.fieldName,
              comment,
              filterCommentKey,
            );
            break;
          case COMMENT_MODE.DELETE:
            // Delete a secondary comment
            await deleteSecondaryComment(
              userId,
              PrimaryFormNames.Releases,
              releaseId,
              secondaryFormName,
              secondaryFormId,
              comment.fieldName,
              filterCommentKey,
            );
            break;
        }
        // Update the comment state counter after any operation
        await updateCommentState(secondaryFormName);
      } finally {
        null; // Placeholder for any cleanup (currently not required)
      }
    };

  /**
   * Returns the form state for a secondary form based on user role and release status.
   *
   * If the user is not an admin and the release status is `ChangesNeeded`, specific rules are applied
   * to the form's read-only state and comments.
   */
  return (callbackProps) => {
    if (!isAdmin && release?.status === ReleaseStatus.ChangesNeeded) {
      return {
        ...formState,
        readOnly: {
          ...formState.readOnly,
          fields: false,
          excludeFields: [],
        },
        comments: {
          ...formState.comments,
          commentCallback: commentCallback(callbackProps),
        },
      };
    }

    // Default form state for other roles and statuses
    return {
      ...formState,
      comments: {
        ...formState.comments,
        commentCallback: commentCallback(callbackProps),
      },
    };
  };
};
